// ── 한국 지도 SVG + 레이아웃 헬퍼 ─────────────────────────────────────────────
import React from 'react';
import { nameColor, REGION_COORDS, extractSido } from './utils';

/**
 * 이름 배열로부터 겹치지 않는 단일 레이블 생성
 * (성이 다르면 성만, 같으면 끝 글자 사용)
 */
export function makeUniqueLabels(names) {
  const result = {};
  const bySurname = {};
  names.forEach(name => {
    const s = name.charAt(0);
    if (!bySurname[s]) bySurname[s] = [];
    bySurname[s].push(name);
  });
  names.forEach(name => {
    const s = name.charAt(0);
    result[name] = bySurname[s].length === 1 ? s : name.charAt(name.length - 1) || s;
  });
  return result;
}

/**
 * 위경도 포인트 배열의 겹침 제거 (최대 80회 반복)
 * @param {Array} points - { lat, lng, ... }[]
 * @param {number} minDeg - 최소 거리 (도 단위)
 */
export function avoidCollisions(points, minDeg = 0.28) {
  if (points.length <= 1) return points;
  const pts = points.map(p => ({ ...p }));
  for (let iter = 0; iter < 80; iter++) {
    let moved = false;
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[j].lng - pts[i].lng;
        const dy = pts[j].lat - pts[i].lat;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < minDeg) {
          const push = (minDeg - dist) / 2;
          const nx = dist > 0 ? dx / dist : 1;
          const ny = dist > 0 ? dy / dist : 0;
          pts[i].lng -= nx * push; pts[i].lat -= ny * push;
          pts[j].lng += nx * push; pts[j].lat += ny * push;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  return pts;
}

/**
 * 마케터 목록에서 시/도별로 가장 많은 지역 하나만 선택
 */
export function pickOnePerPerson(list) {
  const acc = {};
  (list || []).forEach((m) => {
    const sido = extractSido(m.region);
    if (!sido) return;
    if (!acc[m.name]) acc[m.name] = {};
    acc[m.name][sido] = (acc[m.name][sido] || 0) + m.count;
  });
  return Object.entries(acc)
    .map(([name, sidoMap]) => {
      const [sido, count] = Object.entries(sidoMap).sort(([, a], [, b]) => b - a)[0];
      return { name, sido, count };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'ko-KR'));
}

/**
 * SVG 원 레이아웃 계산 (겹침 방지 반복)
 */
export function layoutCircles(markers) {
  const R = 11;
  const circles = markers.map((m) => {
    const coord = REGION_COORDS[m.sido] || { x: 100, y: 130 };
    return { name: m.name, sido: m.sido, count: m.count, cx: coord.x, cy: coord.y, r: R };
  });
  // 같은 시/도에 여러 마커 → 가로로 펼치기
  const bySido = {};
  circles.forEach((c) => {
    if (!bySido[c.sido]) bySido[c.sido] = [];
    bySido[c.sido].push(c);
  });
  Object.values(bySido).forEach((group) => {
    if (group.length <= 1) return;
    group.forEach((c, i) => { c.cx += (i - (group.length - 1) / 2) * (R * 2 + 3); });
  });
  // 원 겹침 제거 반복
  for (let iter = 0; iter < 80; iter++) {
    let moved = false;
    for (let i = 0; i < circles.length; i++) {
      for (let j = i + 1; j < circles.length; j++) {
        const a = circles[i], b = circles[j];
        const dx = b.cx - a.cx, dy = b.cy - a.cy;
        const distSq = dx * dx + dy * dy;
        const minDist = a.r + b.r + 2;
        if (distSq < minDist * minDist) {
          const dist = Math.sqrt(distSq) || 0.01;
          const push = (minDist - dist) / 2;
          const nx = dx / dist, ny = dy / dist;
          a.cx -= nx * push; a.cy -= ny * push;
          b.cx += nx * push; b.cy += ny * push;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  // SVG 경계 클램핑
  circles.forEach((c) => {
    c.cx = Math.max(c.r + 1, Math.min(200 - c.r - 1, c.cx));
    c.cy = Math.max(c.r + 1, Math.min(260 - c.r - 1, c.cy));
  });
  return circles;
}

/**
 * 한국 지도 SVG — 원형 마커 오버레이
 * Props: circles (layoutCircles 결과), emptyText
 */
export function KoreaMapSvg({ circles, emptyText }) {
  return (
    <div className="relative w-full" style={{ paddingTop: '130%' }}>
      <svg viewBox="0 0 200 260" className="absolute top-0 left-0 w-full h-full">
        {/* 한반도 윤곽 */}
        <path
          d="M75,32 L95,22 L130,18 L155,20 L188,28 L192,55 L196,90 L193,128 L185,158 L180,168 L168,180 L148,188 L120,192 L96,188 L75,183 L58,173 L48,152 L52,132 L44,108 L50,85 L58,65 L68,45 Z"
          fill="#dbeafe" stroke="#93c5fd" strokeWidth={1.2}
        />
        {/* 제주도 */}
        <ellipse cx="85" cy="238" rx="17" ry="7" fill="#dbeafe" stroke="#93c5fd" strokeWidth={1} />
        {/* 마커 또는 빈 메시지 */}
        {circles.length === 0 ? (
          <text x="100" y="130" textAnchor="middle" fontSize={10} fill="var(--text-muted)">{emptyText}</text>
        ) : (
          circles.map(({ name, sido, count, cx, cy, r }) => {
            const color = nameColor(name);
            return (
              <g key={`${sido}-${name}`}>
                <circle cx={cx} cy={cy} r={r} fill={color} opacity={0.92} stroke="#fff" strokeWidth={2} />
                {count > 1 && (
                  <text x={cx} y={cy + 4} textAnchor="middle" fontSize={9} fontWeight={800} fill="#fff">{count}</text>
                )}
              </g>
            );
          })
        )}
      </svg>
    </div>
  );
}
